import * as React from 'react';
import { isVoipEnabled, userSession } from 'state/session';
import { myCameraStream } from 'state/video';
import logger from 'utils/log';
import { tenantConfig } from 'utils/tenant';
import { voipRtcConnection } from 'utils/ui/rtc';
import type { TRTCConnection } from 'webrtc/types';

/**
 * Active-speaker ranking. Centralised so consumers (component layer, the hook
 * itself, and any future ranking-aware feature) share a single source of truth.
 * Returns 0 for speakers that should sort first, 1 otherwise.
 */
const speakerRank = (remote: TRTCConnection): number => (remote.isActiveSpeaker || remote.lastActiveSpeaker ? 0 : 1);

const useVoipConnections = () => {
  userSession().useState();
  tenantConfig().useState();
  myCameraStream().useState();

  const { remoteConnections } = voipRtcConnection?.store?.useState() || { remoteConnections: {} };
  const connections = Object.values(remoteConnections);
  const [activeConnectionId, setActiveConnectionId] = React.useState<string>('');

  React.useEffect(() => {
    if (connections.length > 0 && voipRtcConnection) {
      logger.log('[VOIP] SingularVOIP - Publishing user stream...');
      voipRtcConnection.publish();
    }
  }, [connections.length]);

  React.useEffect(() => {
    if (connections.length > 0) {
      setActiveConnectionId((prev) => {
        const newActive = connections.find((remote) => speakerRank(remote) === 0);
        if (newActive && newActive.connectionId !== prev) {
          return newActive.connectionId;
        }
        return prev || connections[0].connectionId;
      });
    } else {
      setActiveConnectionId('');
    }
  }, [connections]);

  // Active/last-active speakers first, others after. Derived here so the
  // component layer stays presentational and ranking rules have one home.
  const sortedRemotes = React.useMemo<TRTCConnection[]>(
    () => (remoteConnections ? Object.values(remoteConnections).sort((a, b) => speakerRank(a) - speakerRank(b)) : []),
    [remoteConnections]
  );

  // `true` when any remote is currently the active speaker — used by the pill
  // to pulse without re-mounting this hook in the collapsed view.
  const isAnyActiveSpeaker = React.useMemo(
    () => sortedRemotes.some((remote) => Boolean(remote.isActiveSpeaker)),
    [sortedRemotes]
  );

  // Phone (≤629px) visibility fallback: exactly ONE tile is shown. Priority:
  // active-speaker remote → first remote with camera on → self if camera on → none.
  // Consumed by the `ex3d-video--phone-hidden` class on each tile.
  const isSelfVideoEnabled = !!myCameraStream().get('isVideoEnabled');
  const phoneDisplayedKey = React.useMemo<string | null>(() => {
    const activeRemote = sortedRemotes.find((r) => r.isActiveSpeaker);
    if (activeRemote) return `remote:${activeRemote.connectionId}`;
    const cameraOnRemote = sortedRemotes.find((r) => !r.videoMuted && r.stream instanceof MediaStream);
    if (cameraOnRemote) return `remote:${cameraOnRemote.connectionId}`;
    if (isSelfVideoEnabled) return 'self';
    return null;
  }, [sortedRemotes, isSelfVideoEnabled]);

  return {
    remoteConnections,
    activeConnectionId,
    sortedRemotes,
    isAnyActiveSpeaker,
    phoneDisplayedKey,
    canShowVoip: tenantConfig().isAllowed('voip') && isVoipEnabled(),
  };
};

export default useVoipConnections;
